/**
 * Tab de configuração de disponibilidade semanal de um Event Type
 * 
 * Exibe uma grade com os 7 dias da semana (0=Dom...6=Sáb)
 * onde o usuário pode ativar/desativar dias e definir horários.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Save } from 'lucide-react';
import { useSchedulingAvailability, type SchedulingAvailability } from '@/hooks/useSchedulingEventTypes';

/** Nomes dos dias da semana em português */
const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

interface EventTypeAvailabilityTabProps {
  eventTypeId: string;
}

export function EventTypeAvailabilityTab({ eventTypeId }: EventTypeAvailabilityTabProps) {
  const { availability, isLoading, updateAvailability, isUpdating } = useSchedulingAvailability(eventTypeId);

  // Estado local editável da disponibilidade
  const [localAvailability, setLocalAvailability] = useState<SchedulingAvailability[]>([]);

  /** Sincroniza estado local quando dados são carregados */
  useEffect(() => {
    if (availability.length > 0) {
      setLocalAvailability([...availability]);
    }
  }, [availability]);

  /** Atualiza um campo específico de um dia */
  const updateDay = (dayIndex: number, field: keyof SchedulingAvailability, value: any) => {
    setLocalAvailability((prev) =>
      prev.map((item) =>
        item.day_of_week === dayIndex ? { ...item, [field]: value } : item
      )
    );
  };

  /** Salva todas as alterações */
  const handleSave = async () => {
    const updates = localAvailability.map((item) => ({
      id: item.id,
      is_available: item.is_available,
      start_time: item.start_time,
      end_time: item.end_time,
    }));
    await updateAvailability(updates);
  };

  if (isLoading) {
    return (
      <Card className="border-border/50">
        <CardContent className="p-6 space-y-3">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle className="text-lg">Horários de Disponibilidade</CardTitle>
        <CardDescription>
          Defina os horários em que você está disponível para agendamentos em cada dia da semana
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {localAvailability.map((item) => (
          <div
            key={item.id}
            className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
              item.is_available
                ? 'border-border/50 bg-background'
                : 'border-border/30 bg-muted/30'
            }`}
          >
            {/* Toggle + Nome do dia */}
            <div className="flex items-center gap-3 w-32 shrink-0">
              <Switch
                checked={item.is_available}
                onCheckedChange={(v) => updateDay(item.day_of_week, 'is_available', v)}
                className="scale-90"
              />
              <span
                className={`text-sm font-medium ${
                  item.is_available ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {DAY_NAMES[item.day_of_week]}
              </span>
            </div>

            {/* Horários (visíveis apenas quando ativo) */}
            {item.is_available ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  type="time"
                  value={item.start_time}
                  onChange={(e) => updateDay(item.day_of_week, 'start_time', e.target.value)}
                  className="w-32"
                />
                <span className="text-muted-foreground text-sm">até</span>
                <Input
                  type="time"
                  value={item.end_time}
                  onChange={(e) => updateDay(item.day_of_week, 'end_time', e.target.value)}
                  className="w-32"
                />
              </div>
            ) : (
              <span className="text-sm text-muted-foreground italic">Indisponível</span>
            )}
          </div>
        ))}

        {/* Botão Salvar */}
        <div className="flex justify-end pt-4">
          <Button onClick={handleSave} disabled={isUpdating} className="gap-2">
            <Save className="h-4 w-4" />
            {isUpdating ? 'Salvando...' : 'Salvar Disponibilidade'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
